import React from "react";

import { PassioFood } from "./types";

/**
 * "Find missing info on the web" helper. When a record is missing data that a
 * reviewer often has to hunt down manually (a UPC, nutrition facts), this opens
 * a targeted Google search in a new tab so they can look it up and fill it in.
 *
 * Renders as a compact row of buttons; only shows the targeted lookups that are
 * actually relevant (e.g. the UPC search only when there is no UPC).
 */
export interface WebLookupProps {
  food: PassioFood;
  /** Render style: compact inline buttons (header) or a labelled row. */
  variant?: "inline" | "row";
  className?: string;
}

const openSearch = (query: string) => {
  const q = query.trim();
  if (!q) return;
  window.open(
    `https://www.google.com/search?q=${encodeURIComponent(q)}`,
    "_blank",
    "noopener,noreferrer"
  );
};

const upcOfFood = (food: PassioFood): string => {
  const b = food.branded || {};
  return String(b.productCode || b.upc || "").trim();
};

export const WebLookup: React.FC<WebLookupProps> = ({
  food,
  variant = "inline",
  className = "",
}) => {
  const name = String(food.name || "").trim();
  const brand = String(food.brandName || food.branded?.owner || "").trim();
  const base = [brand, name].filter(Boolean).join(" ").trim();

  // Nothing to search for without at least a name/brand.
  if (!base) return null;

  const hasUpc = !!upcOfFood(food);
  const hasNutrients =
    Array.isArray(food.nutrients) && food.nutrients.length > 0;

  const btn =
    "inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900";

  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 ${className}`}
      title="Open a web search to find missing product info"
    >
      {variant === "row" && (
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          Find on web
        </span>
      )}
      {!hasUpc && (
        <button
          type="button"
          className={btn}
          onClick={() => openSearch(`${base} UPC barcode`)}
        >
          🔍 Find UPC
        </button>
      )}
      {!hasNutrients && (
        <button
          type="button"
          className={btn}
          onClick={() => openSearch(`${base} nutrition facts`)}
        >
          🔍 Find nutrition facts
        </button>
      )}
      <button
        type="button"
        className={btn}
        onClick={() => openSearch(base)}
        title="Search the web for this product"
      >
        🔍 Search product
      </button>
    </div>
  );
};

export default WebLookup;
